import { useEffect, useState } from "react";
import { Button, Input, Stack } from "@salesapp/ui";
import { crmApi, type Company } from "../../lib/crmApi";
import { FIELD_HINTS } from "../../help/fieldHints";

export function CompanyLookup({
  orgId,
  value,
  companyId,
  onChange,
  allowCreate = false,
  excludeId,
  label = "Company",
}: {
  orgId: string;
  value: string;
  companyId: string;
  onChange: (next: { name: string; companyId: string }) => void;
  allowCreate?: boolean;
  excludeId?: string;
  label?: string;
}) {
  const [matches, setMatches] = useState<Company[]>([]);

  useEffect(() => {
    if (!value.trim() || companyId) {
      setMatches([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .listCompanies(orgId, value.trim())
        .then((rows) => setMatches(rows.filter((company) => company.id !== excludeId)))
        .catch(() => setMatches([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgId, value, companyId, excludeId]);

  return (
    <Stack gap={2}>
      <Input
        label={label}
        tooltip={FIELD_HINTS.company_lookup}
        value={value}
        onChange={(event) => onChange({ name: event.target.value, companyId: "" })}
        placeholder="Start typing a company name"
      />
      {companyId ? (
        <p className="crm-help">Linked to an existing company record.</p>
      ) : value.trim() ? (
        <p className="crm-help">
          {allowCreate
            ? "No exact selection yet — a new incomplete company will be created if you continue."
            : "Select a matching company from the list to continue."}
        </p>
      ) : null}
      {matches.length > 0 && !companyId ? (
        <ul className="crm-lookup-list">
          {matches.map((company) => (
            <li key={company.id}>
              <Button
                variant="ghost"
                onClick={() => onChange({ name: company.legal_name, companyId: company.id })}
              >
                {company.company_code ? `${company.company_code} · ` : ""}
                {company.legal_name}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Stack>
  );
}
